import React, { useCallback, useEffect, useState } from 'react';
import { authApi, ApiError } from '../services/authApi';
import { authStorage } from '../services/authStorage';
import { FaqEntry } from '../types/auth';
import '../styles/FAQ.css';

interface FAQProps {
  isAdmin?: boolean;
}

const FAQ: React.FC<FAQProps> = ({ isAdmin = false }) => {
  const [entries, setEntries] = useState<FaqEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openId, setOpenId] = useState<number | null>(null);

  // Admin form state
  const [showForm, setShowForm] = useState(false);
  const [editingEntry, setEditingEntry] = useState<FaqEntry | null>(null);
  const [formQuestion, setFormQuestion] = useState('');
  const [formAnswer, setFormAnswer] = useState('');
  const [formSaving, setFormSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const loadEntries = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const token = authStorage.getAccessToken();
      if (!token) { setError('Not authenticated'); setLoading(false); return; }
      const data = await authApi.getFaqEntries(token);
      setEntries(data);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load FAQ entries');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadEntries(); }, [loadEntries]);

  const openAdd = () => {
    setEditingEntry(null);
    setFormQuestion('');
    setFormAnswer('');
    setFormError('');
    setShowForm(true);
  };

  const openEdit = (entry: FaqEntry) => {
    setEditingEntry(entry);
    setFormQuestion(entry.question);
    setFormAnswer(entry.answer);
    setFormError('');
    setShowForm(true);
  };

  const cancelForm = () => {
    setShowForm(false);
    setEditingEntry(null);
    setFormError('');
  };

  const saveForm = async () => {
    if (!formQuestion.trim() || !formAnswer.trim()) {
      setFormError('Both question and answer are required.');
      return;
    }
    try {
      setFormSaving(true);
      setFormError('');
      const token = authStorage.getAccessToken();
      if (!token) { setFormError('Not authenticated'); return; }
      if (editingEntry) {
        await authApi.updateFaqEntry(token, editingEntry.id, {
          question: formQuestion.trim(),
          answer: formAnswer.trim(),
        });
      } else {
        await authApi.createFaqEntry(token, {
          question: formQuestion.trim(),
          answer: formAnswer.trim(),
          sequenceNumber: entries.length,
        });
      }
      setShowForm(false);
      setEditingEntry(null);
      await loadEntries();
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : 'Failed to save');
    } finally {
      setFormSaving(false);
    }
  };

  const deleteEntry = async (entry: FaqEntry) => {
    if (!window.confirm(`Delete this FAQ entry?\n\n"${entry.question}"`)) return;
    try {
      const token = authStorage.getAccessToken();
      if (!token) return;
      await authApi.deleteFaqEntry(token, entry.id);
      await loadEntries();
      if (openId === entry.id) setOpenId(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to delete');
    }
  };

  const toggleOpen = (id: number) => {
    setOpenId(prev => (prev === id ? null : id));
  };

  if (loading) {
    return (
      <div className="faq-section">
        <div className="faq-loading">
          <div className="faq-spinner"></div>
          <p>Loading FAQ...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="faq-section">
      <div className="faq-header">
        <div className="faq-header-left">
          <h1>FAQ</h1>
          <p className="faq-subtitle">
            {isAdmin
              ? 'Manage frequently asked questions visible to all users.'
              : 'Find answers to common questions below.'}
          </p>
        </div>
        {isAdmin && !showForm && (
          <button className="faq-add-btn" onClick={openAdd}>
            + Add Question
          </button>
        )}
      </div>

      {error && (
        <div className="faq-error">
          <span>⚠️ {error}</span>
          <button onClick={loadEntries} className="faq-retry-btn">Retry</button>
        </div>
      )}

      {/* Admin form */}
      {showForm && (
        <div className="faq-form">
          <h3>{editingEntry ? 'Edit Question' : 'New Question'}</h3>
          <div className="faq-form-field">
            <label>Question</label>
            <input
              type="text"
              value={formQuestion}
              onChange={e => setFormQuestion(e.target.value)}
              placeholder="Enter the question..."
              className="faq-form-input"
              maxLength={500}
            />
          </div>
          <div className="faq-form-field">
            <label>Answer</label>
            <textarea
              value={formAnswer}
              onChange={e => setFormAnswer(e.target.value)}
              placeholder="Enter the answer..."
              className="faq-form-textarea"
              rows={6}
            />
          </div>
          {formError && <p className="faq-form-error">{formError}</p>}
          <div className="faq-form-actions">
            <button className="faq-cancel-btn" onClick={cancelForm} disabled={formSaving}>
              Cancel
            </button>
            <button className="faq-save-btn" onClick={saveForm} disabled={formSaving}>
              {formSaving ? 'Saving...' : editingEntry ? 'Save Changes' : 'Add FAQ'}
            </button>
          </div>
        </div>
      )}

      {/* FAQ accordion list */}
      {entries.length === 0 && !showForm ? (
        <div className="faq-empty">
          <div className="faq-empty-icon">❓</div>
          <p>{isAdmin ? 'No FAQ entries yet. Click "Add Question" to create one.' : 'No FAQ entries available yet.'}</p>
        </div>
      ) : (
        <div className="faq-list">
          {entries.map((entry, index) => (
            <div
              key={entry.id}
              className={`faq-item ${openId === entry.id ? 'open' : ''} ${!entry.isActive ? 'inactive' : ''}`}
            >
              <button
                className="faq-question-btn"
                onClick={() => toggleOpen(entry.id)}
                aria-expanded={openId === entry.id}
              >
                <span className="faq-q-number">Q{index + 1}</span>
                <span className="faq-q-text">{entry.question}</span>
                <span className="faq-chevron">{openId === entry.id ? '▲' : '▼'}</span>
              </button>
              {openId === entry.id && (
                <div className="faq-answer">
                  <div className="faq-answer-content">
                    {entry.answer.split('\n').map((line, i) => (
                      <p key={i}>{line}</p>
                    ))}
                  </div>
                  {isAdmin && (
                    <div className="faq-item-actions">
                      <button className="faq-edit-btn" onClick={() => openEdit(entry)}>✏️ Edit</button>
                      <button className="faq-delete-btn" onClick={() => deleteEntry(entry)}>🗑️ Delete</button>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default FAQ;

